import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Zap, Plus, Loader2, Link2, Pencil, AlertTriangle, Landmark, Sparkles, Activity, FileText } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { useToast } from '../../../src/contexts/ToastContext';
import { useAuth } from '../../../src/contexts/AuthContext';
import { Rateio, RateioItem, RateioUnidadeGeradora } from '../../../types';
import { useRateioBalanceamento, UcParaRateio, RateioRowState, PriorityRow } from '../../../src/hooks/useRateioBalanceamento';
import { buscarHistoricoConsumoUcs, buscarPercentualAnteriorUcs, FaturaConsumoResumo } from './consumoHistorico';
import { consumoParaAlocacao, consumoOperacionalDaUc } from '../../../src/lib/consumoDaUc';
import { avaliarTravamento } from '../../../src/lib/consumoOperacional';
import { projetarConsumoUc } from './projecaoUc';
import { mesReferenciaParaOrdinal } from './mesReferenciaRateio';
import { detectarTitularidadeDivergente, detectarCreditosDuplicados, mapearCoberturaSaldo } from './alertasRateio';
import { travarUcExcessoSaldo } from './rateioTravamentoActions';
import { buscarProducaoRealUg } from './telemetriaActions';
import { buscarGeracaoFaturaUg } from './faturaGeradoraActions';
import UGFormModal from './UGFormModal';
import VincularUCsModal from './VincularUCsModal';
import RateioTabelaUCs from './RateioTabelaUCs';
import RateioStatusWorkflow from './RateioStatusWorkflow';
import UGAprovacaoCard from './UGAprovacaoCard';
import AlertasBanner from './AlertasBanner';
import UcsTravadasCard from './UcsTravadasCard';
import UcsAprovadasPendentesCard from './UcsAprovadasPendentesCard';
import AuditoriaLogPanel from './AuditoriaLogPanel';
import InfoTip from '../../../src/components/InfoTip';

const SITUACOES_ELEGIVEIS = ['Ativa', 'Ativa-S/-Rateio', '1° Ciclo', '2° Ciclo', 'Reativacao'];

const generateMonths = () => {
  const months: string[] = [];
  const date = new Date();
  date.setMonth(date.getMonth() - 3);
  const formatter = new Intl.DateTimeFormat('pt-BR', { month: 'short', year: 'numeric' });
  for (let i = 0; i < 15; i++) {
    const parts = formatter.formatToParts(date);
    const m = parts.find(p => p.type === 'month')?.value.toUpperCase() || '';
    const y = parts.find(p => p.type === 'year')?.value || '';
    months.push(`${m}/${y}`);
    date.setMonth(date.getMonth() + 1);
  }
  return months;
};

const currentMonth = () => {
  const formatter = new Intl.DateTimeFormat('pt-BR', { month: 'short', year: 'numeric' });
  const parts = formatter.formatToParts(new Date());
  const m = parts.find(p => p.type === 'month')?.value.toUpperCase() || '';
  const y = parts.find(p => p.type === 'year')?.value || '';
  return `${m}/${y}`;
};

const formatKwh = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Quanto a carteira desta UG deve consumir no próximo mês, projetado a partir do
 * histórico real de faturas (mesmo mês do ano anterior, corrigido por tendência).
 *
 * Some apenas as UCs com base real. Completar as demais com a média inflaria o
 * total e esconderia justamente o que o analista precisa saber — de quantas UCs
 * o sistema realmente sabe alguma coisa. Por isso a cobertura vem no rótulo.
 */
const ProjecaoCarteira: React.FC<{ kwh: number; comBase: number; total: number }> = ({ kwh, comBase, total }) => {
  if (comBase === 0) return null;
  const parcial = comBase < total;
  return (
    <span className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
      <Activity size={14} className="text-primary" />
      Projeção do próximo mês: <strong className="tabular-nums">{formatKwh(kwh)} kWh</strong>
      <span className="text-xs text-gray-400 dark:text-gray-500">
        ({comBase} de {total} {total === 1 ? 'UC' : 'UCs'})
      </span>
      {parcial && (
        <InfoTip label="Quais UCs ficaram de fora desta projeção" tone="warning">
          {total - comBase} de {total} UCs ainda não têm histórico suficiente (mínimo de 3 faturas) e ficaram de fora
          da projeção. O total acima soma só quem tem base real.
        </InfoTip>
      )}
    </span>
  );
};

const SimuladorRateioView: React.FC = () => {
  const { addToast } = useToast();
  const { profile } = useAuth();

  const [ugs, setUgs] = useState<RateioUnidadeGeradora[]>([]);
  const [loadingUgs, setLoadingUgs] = useState(true);
  const [selectedUgId, setSelectedUgId] = useState<string | null>(null);
  const [showUgForm, setShowUgForm] = useState(false);
  const [editingUg, setEditingUg] = useState<RateioUnidadeGeradora | null>(null);

  const [mesReferencia, setMesReferencia] = useState(currentMonth());
  const [excedenteInput, setExcedenteInput] = useState('');
  const [ucsVinculadas, setUcsVinculadas] = useState<UcParaRateio[]>([]);
  const [loadingUcs, setLoadingUcs] = useState(false);
  const [showVincularModal, setShowVincularModal] = useState(false);
  const [rateio, setRateio] = useState<Rateio | null>(null);
  const [itensSalvos, setItensSalvos] = useState<RateioItem[]>([]);
  const [saldoAncoraUg, setSaldoAncoraUg] = useState(0);
  const [historicoPorUc, setHistoricoPorUc] = useState<Map<number, FaturaConsumoResumo[]>>(new Map());
  const [percentualAnteriorPorUc, setPercentualAnteriorPorUc] = useState<Map<number, number>>(new Map());
  const [saving, setSaving] = useState(false);
  const [buscandoProducaoReal, setBuscandoProducaoReal] = useState(false);
  const [buscandoGeracaoFatura, setBuscandoGeracaoFatura] = useState(false);
  const [creditosDuplicados, setCreditosDuplicados] = useState<Set<number>>(new Set());
  const [saldosAncoraPorUg, setSaldosAncoraPorUg] = useState<Map<string, number>>(new Map());
  const travamentoTentado = useRef<Set<number>>(new Set());

  const meses = useMemo(() => generateMonths(), []);
  const selectedUg = ugs.find(u => u.id === selectedUgId) || null;
  const excedenteAlocavelKwh = parseFloat(excedenteInput.replace(',', '.')) || 0;
  const isReadOnly = !!rateio && rateio.status !== 'rascunho';

  // UCs "Pausada" (travamento automático por excesso de saldo ou pausa manual —
  // Seção 9/13.3) saem da grade principal (que soma 100%) e vão para o card
  // "UCs Travadas" abaixo, somente leitura. useRateioBalanceamento nunca vê essas UCs.
  const ucsElegiveis = useMemo(() => ucsVinculadas.filter(u => u.situacao !== 'Pausada'), [ucsVinculadas]);
  const ucsPausadas = useMemo(() => ucsVinculadas.filter(u => u.situacao === 'Pausada'), [ucsVinculadas]);
  const titularidadeDivergente = useMemo(
    () => new Set(detectarTitularidadeDivergente(ucsElegiveis).map(u => u.id)),
    [ucsElegiveis]
  );

  // Cobertura do saldo de créditos por UC (solicitação do CEO, 27/08/2026): abaixo
  // de 1 mês a UC entra no próximo ciclo sem lastro e o Analista precisa ver isso
  // antes de fechar o rateio. Sai do histórico de faturas que a tela já carregou
  // — nenhuma consulta nova — e não dispara ação nenhuma: é alerta, não regra.
  // O travamento por EXCESSO (cobertura > 3 meses), no efeito mais abaixo, mede a
  // mesma coisa no sentido oposto e agora lê o mesmo campo — o limiar dele
  // continua em 3, o que mudou foi o saldo deixar de ser o resultado do mês.
  const coberturaSaldoPorUc = useMemo(
    () => mapearCoberturaSaldo(ucsElegiveis, historicoPorUc),
    [ucsElegiveis, historicoPorUc]
  );
  const saldoBaixoCount = useMemo(
    () => Array.from(coberturaSaldoPorUc.values()).filter(c => c.abaixoDoMinimo).length,
    [coberturaSaldoPorUc]
  );

  // Sugestão de UG com maior sobra (Seção 10 — "sugerir a UG com maior sobra de
  // energia, regra simples"). Usa o saldo acumulado na Âncora como proxy de sobra.
  const ugComMaiorSobraId = useMemo(() => {
    let melhorId: string | null = null;
    let melhorSaldo = 0;
    for (const ug of ugs) {
      if (!ug.ativo || ug.status_aprovacao !== 'aprovada') continue;
      const saldo = saldosAncoraPorUg.get(ug.id) || 0;
      if (saldo > melhorSaldo) {
        melhorSaldo = saldo;
        melhorId = ug.id;
      }
    }
    return melhorId;
  }, [ugs, saldosAncoraPorUg]);

  const {
    modalidade,
    gargalo,
    rows,
    ordemPrioridade,
    ancoraKwh,
    soma,
    habilitaSalvar,
    inicializar,
    editarPercentual,
    toggleLock,
    arrastar,
    setGargalo,
    trocarModalidade,
    hidratar,
    aplicarSugestaoDelta,
  } = useRateioBalanceamento(excedenteAlocavelKwh);

  const somaConsumoMedioUcs = ucsElegiveis.reduce((s, u) => s + consumoParaAlocacao(u), 0);

  // Projeção do próximo mês por UC, a partir do histórico de faturas que a tela
  // já carregou em lote — nada é persistido, para não criar uma segunda verdade.
  // É o que dá previsibilidade à decisão de quanto injetar: o analista deixa de
  // alocar só olhando para trás.
  const projecaoPorUc = useMemo(() => {
    const ordinalAtual = mesReferenciaParaOrdinal(mesReferencia);
    const mapa = new Map<number, ReturnType<typeof projetarConsumoUc>>();
    if (ordinalAtual == null) return mapa;
    for (const uc of ucsElegiveis) {
      mapa.set(
        uc.id,
        projetarConsumoUc({
          historico: historicoPorUc.get(uc.id) || [],
          ordinalAtual,
          horizonteMeses: 1,
          tipoLigacao: (uc as { tipo_ligacao?: string }).tipo_ligacao,
        }),
      );
    }
    return mapa;
  }, [ucsElegiveis, historicoPorUc, mesReferencia]);

  // Só somam as UCs com base real: projeção que caiu na média por falta de
  // histórico não descreve o mês que vem, e inflar o total esconderia isso.
  const projecaoCarteira = useMemo(() => {
    let kwh = 0;
    let comBase = 0;
    for (const p of projecaoPorUc.values()) {
      const mes = p.projecao[0];
      if (!mes || p.confianca === 'sem_dados' || p.mesesObservados < 3) continue;
      kwh += mes.consumoProjetadoKwh;
      comBase++;
    }
    return { kwh, comBase, total: projecaoPorUc.size };
  }, [projecaoPorUc]);
  const mostrarSeletorGargalo = modalidade === 'ordem_prioridade' && somaConsumoMedioUcs > excedenteAlocavelKwh;
  const ancoraPercent = excedenteAlocavelKwh > 0 ? (ancoraKwh / excedenteAlocavelKwh) * 100 : 0;

  // Regra de Ouro (soma = 100%) continua sendo o que valida/libera o "Salvar
  // Rateio" — isso não muda. O que muda é o número em destaque no rodapé: em
  // vez de repetir "100.00%" (redundante, sempre bate quando a Regra de Ouro
  // fecha), mostra quanto da CAPACIDADE INSTALADA da UG (kWp) o Consumo Médio
  // das UCs vinculadas representa — usa consumo médio (não energia alocada por
  // percent × Excedente) pra não depender do Excedente Alocável estar preenchido.
  const capacidadeKwpSelecionada = selectedUg?.capacidade_kwp || 0;
  const percentDaCapacidadeUg = capacidadeKwpSelecionada > 0 ? (somaConsumoMedioUcs / capacidadeKwpSelecionada) * 100 : 0;

  const carregarUgs = async () => {
    setLoadingUgs(true);
    try {
      const [{ data, error }, { data: saldosData, error: saldosError }] = await Promise.all([
        supabase
          .from('rateio_unidades_geradoras')
          .select('*, distribuidora_config(nome, email_protocolo, dia_leitura_padrao, colunas_planilha)')
          .order('nome'),
        supabase.from('rateio_ancora_saldo_ug').select('rateio_unidade_geradora_id, saldo_ancora_kwh'),
      ]);
      if (error) throw error;
      if (saldosError) throw saldosError;
      setUgs((data || []) as RateioUnidadeGeradora[]);
      setSaldosAncoraPorUg(
        new Map((saldosData || []).map((s: any) => [s.rateio_unidade_geradora_id as string, s.saldo_ancora_kwh as number]))
      );
    } catch (error) {
      console.error('Error loading UGs', error);
      addToast('Erro ao carregar UGs.', 'error');
    } finally {
      setLoadingUgs(false);
    }
  };

  useEffect(() => {
    carregarUgs();
  }, []);

  const carregarSaldoAncora = async (ugId: string) => {
    const { data } = await supabase
      .from('rateio_ancora_saldo_ug')
      .select('saldo_ancora_kwh')
      .eq('rateio_unidade_geradora_id', ugId)
      .maybeSingle();
    setSaldoAncoraUg(data?.saldo_ancora_kwh || 0);
  };

  const carregarUcsEDraft = async (ugId: string, mes: string) => {
    setLoadingUcs(true);
    try {
      const { data: ucsData, error: ucsError } = await supabase
        .from('unidades_consumidoras')
        .select(
          'id, numero_uc, outro_numero_uc, nome_cliente, cpf_cnpj_titular, cpf_cnpj_cliente, situacao, consumo_medio_kwh, consumo_observado_kwh, consumo_observado_meses, consumo_observado_dispersao, consumo_observado_em, rateio_unidade_geradora_id, rateio_travado_em, rateio_motivo_travamento, rateio_destravado_por, rateio_destravado_em, modalidade_gd, rateio_vinculado_em'
        )
        .eq('rateio_unidade_geradora_id', ugId)
        .neq('situacao', 'Cancelada');
      if (ucsError) throw ucsError;

      const ucs = (ucsData || []) as UcParaRateio[];
      setUcsVinculadas(ucs);
      const ucsElegiveisLocal = ucs.filter(u => u.situacao !== 'Pausada');

      buscarHistoricoConsumoUcs(ucs)
        .then(setHistoricoPorUc)
        .catch(error => console.error('Error loading consumption history', error));

      buscarPercentualAnteriorUcs(ugId, ucs.map(u => u.id), mes)
        .then(setPercentualAnteriorPorUc)
        .catch(error => console.error('Error loading previous percentuais', error));

      const { data: rateioData, error: rateioError } = await supabase
        .from('rateios')
        .select('*')
        .eq('rateio_unidade_geradora_id', ugId)
        .eq('mes_referencia', mes)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (rateioError) throw rateioError;

      if (rateioData) {
        const rateioAtual = rateioData as Rateio;
        setRateio(rateioAtual);
        setExcedenteInput(String(rateioAtual.excedente_alocavel_kwh));

        const { data: itensData, error: itensError } = await supabase
          .from('rateio_itens')
          .select('*')
          .eq('rateio_id', rateioAtual.id);
        if (itensError) throw itensError;

        const itens = (itensData || []) as RateioItem[];
        setItensSalvos(itens);

        const rowsSalvas: RateioRowState[] = itens.map(item => ({
          ucId: item.unidade_consumidora_id,
          percent: item.percent_rateio,
          locked: item.locked,
          energiaAlvoKwh: item.energia_alvo_kwh,
        }));
        const ordemSalva: PriorityRow[] = [...itens]
          .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
          .map(item => ({
            ucId: item.unidade_consumidora_id,
            consumoMedioKwh: consumoParaAlocacao(ucs.find(u => u.id === item.unidade_consumidora_id)),
          }));

        hidratar(
          rateioAtual.modalidade,
          rowsSalvas,
          ordemSalva.length > 0
            ? ordemSalva
            : ucsElegiveisLocal.map(u => ({ ucId: u.id, consumoMedioKwh: consumoParaAlocacao(u) })),
          rateioAtual.ancora_kwh || 0,
          rateioAtual.gargalo || null
        );
      } else {
        setRateio(null);
        setItensSalvos([]);
        // Sem rascunho ainda: pré-preenche com a capacidade instalada da UG
        // (ponto de partida — o Analista continua podendo editar se o
        // excedente real do mês for diferente). Não mexe no motor de cálculo.
        const capacidadeUg = ugs.find(u => u.id === ugId)?.capacidade_kwp;
        setExcedenteInput(capacidadeUg ? String(capacidadeUg) : '');
        inicializar(ucsElegiveisLocal);
      }
    } catch (error) {
      console.error('Error loading UCs/draft', error);
      addToast('Erro ao carregar dados da UG.', 'error');
    } finally {
      setLoadingUcs(false);
    }
  };

  useEffect(() => {
    travamentoTentado.current = new Set();
    if (selectedUgId) {
      carregarUcsEDraft(selectedUgId, mesReferencia);
      carregarSaldoAncora(selectedUgId);
    } else {
      setUcsVinculadas([]);
      setRateio(null);
      setItensSalvos([]);
      setSaldoAncoraUg(0);
      setHistoricoPorUc(new Map());
      setPercentualAnteriorPorUc(new Map());
      setCreditosDuplicados(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUgId, mesReferencia]);

  // Travamento automático (Seção 9/13.3): assim que o histórico de consumo
  // carrega, verifica cada UC elegível e trava (via RPC) quem tem cobertura >
  // 3 meses. `travamentoTentado` evita reenviar a mesma UC enquanto a tela não
  // recarrega (o efeito reroda a cada mudança de historicoPorUc/ucsVinculadas).
  useEffect(() => {
    const candidatos = ucsElegiveis.filter(uc => {
      if (!SITUACOES_ELEGIVEIS.includes(uc.situacao || '')) return false;
      if (travamentoTentado.current.has(uc.id)) return false;
      const historico = historicoPorUc.get(uc.id) || [];
      // `avaliarTravamento` no lugar da conta crua: ela carrega as travas que o
      // recálculo diário exige — não pausar com base em estimativa cadastral ou
      // amostra frágil, não retravar por cima de um destravamento recente do
      // gestor, e não encostar em pausa de origem humana. A RPC no banco aplica
      // exatamente as mesmas regras (migration 20260828140000), então tela e
      // servidor não divergem.
      //
      // O saldo é o ESTOQUE de créditos (`saldo_ugs_vigor`), não o resultado do
      // mês: medir por `saldo_acumulado_kwh` era o motivo de esta regra nunca ter
      // pausado ninguém desde que existe. Ver `coberturaSaldo.ts`.
      return avaliarTravamento({
        saldoCreditosKwh: historico[0]?.saldo_creditos_kwh,
        consumo: consumoOperacionalDaUc(uc),
        travadaAtualmente: uc.situacao === 'Pausada',
        motivoTravamento: uc.rateio_motivo_travamento,
        destravadaEm: uc.rateio_destravado_em,
      }).acao === 'travar';
    });
    if (candidatos.length === 0) return;
    candidatos.forEach(uc => travamentoTentado.current.add(uc.id));

    Promise.allSettled(
      candidatos.map(uc => {
        const historico = historicoPorUc.get(uc.id) || [];
        return travarUcExcessoSaldo(uc.id, historico[0]?.saldo_creditos_kwh || 0);
      })
    ).then(resultados => {
      if (resultados.some(r => r.status === 'fulfilled') && selectedUgId) {
        carregarUcsEDraft(selectedUgId, mesReferencia);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historicoPorUc, ucsElegiveis]);

  // Créditos duplicados (Seção 12): checa se alguma UC desta UG já consta num
  // rateio protocolado/conciliado de OUTRA UG no mesmo mês de referência.
  useEffect(() => {
    if (!selectedUgId || ucsElegiveis.length === 0) {
      setCreditosDuplicados(new Set());
      return;
    }
    detectarCreditosDuplicados(ucsElegiveis.map(u => u.id), selectedUgId, mesReferencia)
      .then(setCreditosDuplicados)
      .catch(error => console.error('Error detecting duplicate credits', error));
  }, [selectedUgId, mesReferencia, ucsElegiveis]);

  const handleUgSaved = (ug: RateioUnidadeGeradora) => {
    setShowUgForm(false);
    setEditingUg(null);
    carregarUgs();
    setSelectedUgId(ug.id);
  };

  const handleVinculosAlterados = () => {
    if (selectedUgId) carregarUcsEDraft(selectedUgId, mesReferencia);
  };

  const handleWorkflowChanged = () => {
    if (selectedUgId) {
      carregarUcsEDraft(selectedUgId, mesReferencia);
      carregarSaldoAncora(selectedUgId);
    }
  };

  const handleBuscarProducaoReal = async () => {
    if (!selectedUg) return;
    setBuscandoProducaoReal(true);
    try {
      const resultado = await buscarProducaoRealUg(selectedUg);
      if (!resultado) {
        addToast('Não foi possível obter a produção real dessa UG na telemetria.', 'error');
        return;
      }
      setExcedenteInput(String(resultado.producaoKwh));
      addToast(
        `Excedente Alocável atualizado com a produção real (${formatKwh(resultado.producaoKwh)} kWh, via ${resultado.marca}).`,
        'success'
      );
    } catch (error) {
      console.error('Error fetching real production from telemetry', error);
      addToast('Erro ao buscar produção real na telemetria.', 'error');
    } finally {
      setBuscandoProducaoReal(false);
    }
  };

  const handleBuscarGeracaoFatura = async () => {
    if (!selectedUg?.numero_uc_geradora) return;
    setBuscandoGeracaoFatura(true);
    try {
      const resultado = await buscarGeracaoFaturaUg(selectedUg.numero_uc_geradora);
      if (!resultado) {
        addToast(
          'Nenhuma fatura extraída pra essa UG ainda — extraia em Gestão > Extração de Faturas.',
          'info'
        );
        return;
      }
      setExcedenteInput(String(resultado.geracaoKwh));
      addToast(
        `Excedente Alocável atualizado com a geração da fatura (${formatKwh(resultado.geracaoKwh)} kWh, ciclo ${resultado.mesReferenciaFatura}).`,
        'success'
      );
    } catch (error) {
      console.error('Error fetching generation from extracted fatura', error);
      addToast('Erro ao buscar geração na fatura extraída.', 'error');
    } finally {
      setBuscandoGeracaoFatura(false);
    }
  };

  const handleSalvar = async () => {
    if (!selectedUg || !habilitaSalvar || isReadOnly) return;
    setSaving(true);
    try {
      const ucsById = new Map(ucsVinculadas.map(u => [u.id, u]));
      const itensPayload = rows.map(row => {
        const uc = ucsById.get(row.ucId);
        const ordemIdx =
          modalidade === 'ordem_prioridade' ? ordemPrioridade.findIndex(p => p.ucId === row.ucId) : null;
        return {
          unidade_consumidora_id: row.ucId,
          uc: uc?.numero_uc || '',
          outro_numero_uc: uc?.outro_numero_uc || null,
          nome: uc?.nome_cliente || null,
          cpf_cnpj: uc?.cpf_cnpj_titular || uc?.cpf_cnpj_cliente || null,
          percent_rateio: row.percent,
          energia_alvo_kwh: row.energiaAlvoKwh,
          locked: row.locked,
          ordem: ordemIdx !== null && ordemIdx >= 0 ? ordemIdx : null,
        };
      });

      const { error } = await supabase.rpc('save_rateio_simulacao', {
        p_rateio_unidade_geradora_id: selectedUg.id,
        p_mes_referencia: mesReferencia,
        p_excedente_alocavel_kwh: excedenteAlocavelKwh,
        p_modalidade: modalidade,
        p_usuario_nome: profile?.full_name || null,
        p_itens: itensPayload,
        p_ancora_kwh: modalidade === 'ordem_prioridade' ? ancoraKwh : 0,
        p_gargalo: modalidade === 'ordem_prioridade' ? gargalo : null,
      });
      if (error) throw error;

      addToast('Rateio salvo com sucesso.', 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'gestao', funcionalidade: 'rateio_simulador', acao: 'salvar_rateio_simulacao', origem: 'acao' });
      await carregarUcsEDraft(selectedUg.id, mesReferencia);
    } catch (error) {
      console.error('Error saving rateio', error);
      addToast('Erro ao salvar o rateio.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const ucsById = new Map(ucsVinculadas.map(u => [u.id, u]));

  return (
    <div className="flex flex-col md:flex-row gap-6 animate-fade-in">
      {/* Painel esquerdo: UGs */}
      <aside className="w-full md:w-72 flex-shrink-0">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h2 className="font-poppins font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
              <Zap size={18} className="text-primary" /> UGs
            </h2>
            <button
              onClick={() => {
                setEditingUg(null);
                setShowUgForm(true);
              }}
              className="p-1.5 rounded-lg text-primary hover:bg-primary/10"
              title="Nova UG"
            >
              <Plus size={18} />
            </button>
          </div>
          <div className="p-2 space-y-1 max-h-[70vh] overflow-y-auto">
            {loadingUgs ? (
              <div className="flex justify-center py-6">
                <Loader2 className="animate-spin text-gray-400" size={20} />
              </div>
            ) : ugs.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6 px-2">
                Nenhuma UG cadastrada. Clique em "+" para criar a primeira.
              </p>
            ) : (
              ugs.map(ug => (
                // O lápis de editar fica FORA do botão de selecionar: botão dentro
                // de botão é HTML inválido e o clique no lápis também selecionava.
                <div key={ug.id} className="relative group">
                <button
                  onClick={() => setSelectedUgId(ug.id)}
                  className={`w-full text-left pl-3 pr-9 py-2.5 rounded-xl transition-colors ${
                    selectedUgId === ug.id
                      ? 'bg-primary text-white'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium truncate flex items-center gap-1.5">
                        {ug.nome}
                        {ug.status_aprovacao === 'pendente' && (
                          <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-amber-400" title="Aguardando aprovação" />
                        )}
                        {ug.status_aprovacao === 'rejeitada' && (
                          <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-red-400" title="Rejeitada" />
                        )}
                        {ug.id === ugComMaiorSobraId && (
                          <span title="Maior sobra acumulada — sugestão de alocação para novas UCs" className="shrink-0 inline-flex">
                            <Sparkles size={12} className={selectedUgId === ug.id ? 'text-white' : 'text-secondary'} />
                          </span>
                        )}
                      </p>
                      {ug.distribuidora_config?.nome && (
                        <p className={`text-xs truncate ${selectedUgId === ug.id ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                          {ug.distribuidora_config.nome}
                        </p>
                      )}
                    </div>
                  </div>
                  {ug.status_aprovacao === 'pendente' && (
                    <span className={`text-xs ${selectedUgId === ug.id ? 'text-white/80' : 'text-amber-600 dark:text-amber-400'}`}>
                      Aguardando aprovação
                    </span>
                  )}
                  {ug.status_aprovacao === 'rejeitada' && (
                    <span className={`text-xs ${selectedUgId === ug.id ? 'text-white/80' : 'text-red-600 dark:text-red-400'}`}>
                      Rejeitada
                    </span>
                  )}
                  {!ug.ativo && <span className="text-xs opacity-70">(inativa)</span>}
                </button>
                <button
                  type="button"
                  aria-label={`Editar UG ${ug.nome}`}
                  onClick={() => { setEditingUg(ug); setShowUgForm(true); }}
                  className={`absolute right-2 top-2.5 rounded p-0.5 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${selectedUgId === ug.id ? 'text-white' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'}`}
                >
                  <Pencil size={14} />
                </button>
                </div>
              ))
            )}
          </div>
        </div>
      </aside>

      {/* Painel direito: Simulador */}
      <div className="flex-1 min-w-0">
        {!selectedUg ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center">
            <Zap className="mx-auto text-gray-300 dark:text-gray-600 mb-3" size={40} />
            <p className="text-gray-500 dark:text-gray-400">Selecione uma UG para simular o rateio.</p>
          </div>
        ) : selectedUg.status_aprovacao !== 'aprovada' ? (
          <UGAprovacaoCard ug={selectedUg} onChanged={carregarUgs} />
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white font-poppins">{selectedUg.nome}</h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-3 flex-wrap">
                    <span>{selectedUg.distribuidora_config?.nome || 'Distribuidora não informada'}</span>
                    <span className="flex items-center gap-1">
                      <Landmark size={14} /> Saldo Âncora acumulado: {formatKwh(saldoAncoraUg)} kWh
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={mesReferencia}
                    onChange={e => setMesReferencia(e.target.value)}
                    className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm"
                  >
                    {meses.map(m => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Excedente Alocável (kWh)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={excedenteInput}
                      onChange={e => setExcedenteInput(e.target.value)}
                      placeholder="0,00"
                      disabled={isReadOnly}
                      className="w-40 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                    {selectedUg.telemetria_site_id && !isReadOnly && (
                      <button
                        type="button"
                        onClick={handleBuscarProducaoReal}
                        disabled={buscandoProducaoReal}
                        title="Buscar produção real na telemetria (Growatt/Chint) e usar como Excedente Alocável"
                        className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-primary/30 text-primary text-xs font-medium hover:bg-primary/10 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {buscandoProducaoReal ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Activity size={14} />
                        )}
                        Produção Real
                      </button>
                    )}
                    {selectedUg.numero_uc_geradora && !isReadOnly && (
                      <button
                        type="button"
                        onClick={handleBuscarGeracaoFatura}
                        disabled={buscandoGeracaoFatura}
                        title="Buscar geração da fatura própria da UG já extraída e usar como Excedente Alocável"
                        className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-primary/30 text-primary text-xs font-medium hover:bg-primary/10 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {buscandoGeracaoFatura ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <FileText size={14} />
                        )}
                        Produção (Fatura)
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Modalidade</label>
                  <div className="flex bg-gray-200 dark:bg-gray-700 p-1 rounded-lg">
                    <button
                      onClick={() => !isReadOnly && trocarModalidade('percentual_fixo', ucsVinculadas)}
                      disabled={isReadOnly}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                        modalidade === 'percentual_fixo'
                          ? 'bg-white dark:bg-gray-800 text-primary shadow'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                      }`}
                    >
                      Percentual Fixo
                    </button>
                    <button
                      onClick={() => !isReadOnly && trocarModalidade('ordem_prioridade', ucsVinculadas)}
                      disabled={isReadOnly}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                        modalidade === 'ordem_prioridade'
                          ? 'bg-white dark:bg-gray-800 text-primary shadow'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                      }`}
                    >
                      Ordem de Prioridade
                    </button>
                  </div>
                </div>

                {mostrarSeletorGargalo && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Seletor de Gargalo (déficit)
                    </label>
                    <div className="flex bg-gray-200 dark:bg-gray-700 p-1 rounded-lg">
                      <button
                        onClick={() => !isReadOnly && setGargalo('priorizacao_seletiva')}
                        disabled={isReadOnly}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                          gargalo === 'priorizacao_seletiva'
                            ? 'bg-white dark:bg-gray-800 text-primary shadow'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                        }`}
                      >
                        Priorização Seletiva
                      </button>
                      <button
                        onClick={() => !isReadOnly && setGargalo('reducao_proporcional')}
                        disabled={isReadOnly}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                          gargalo === 'reducao_proporcional'
                            ? 'bg-white dark:bg-gray-800 text-primary shadow'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                        }`}
                      >
                        Redução Proporcional
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6">
              <UcsAprovadasPendentesCard ug={selectedUg} onVinculado={handleVinculosAlterados} />
              {loadingUcs ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="animate-spin text-gray-400" size={24} />
                </div>
              ) : ucsVinculadas.length === 0 ? (
                <div className="text-center py-12">
                  <Link2 className="mx-auto text-gray-300 dark:text-gray-600 mb-3" size={36} />
                  <p className="text-gray-500 dark:text-gray-400 mb-4">Nenhuma UC vinculada a esta UG ainda.</p>
                  <button
                    onClick={() => setShowVincularModal(true)}
                    className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:opacity-90"
                  >
                    Vincular UCs
                  </button>
                </div>
              ) : (
                <>
                  {!isReadOnly && (
                    <div className="flex justify-end mb-3">
                      <button
                        onClick={() => setShowVincularModal(true)}
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        <Link2 size={14} /> Gerenciar UCs vinculadas
                      </button>
                    </div>
                  )}
                  <AlertasBanner
                    titularidadeDivergenteCount={titularidadeDivergente.size}
                    creditosDuplicadosCount={creditosDuplicados.size}
                    saldoBaixoCount={saldoBaixoCount}
                  />
                  <RateioTabelaUCs
                    modalidade={modalidade}
                    rows={rows}
                    ordemPrioridade={ordemPrioridade}
                    ucsById={ucsById}
                    historicoPorUc={historicoPorUc}
                    projecaoPorUc={projecaoPorUc}
                    percentualAnteriorPorUc={percentualAnteriorPorUc}
                    onEditarPercentual={editarPercentual}
                    onToggleLock={toggleLock}
                    onDragEnd={arrastar}
                    onAplicarSugestaoDelta={aplicarSugestaoDelta}
                    onUcAlterada={() => selectedUgId && carregarUcsEDraft(selectedUgId, mesReferencia)}
                    titularidadeDivergente={titularidadeDivergente}
                    creditosDuplicados={creditosDuplicados}
                    coberturaSaldoPorUc={coberturaSaldoPorUc}
                    diaLeituraPadrao={selectedUg.distribuidora_config?.dia_leitura_padrao}
                    readOnly={isReadOnly}
                  />
                  <UcsTravadasCard
                    ucs={ucsPausadas}
                    onChanged={() => selectedUgId && carregarUcsEDraft(selectedUgId, mesReferencia)}
                  />
                </>
              )}
            </div>

            {ucsVinculadas.length > 0 && (!rateio || rateio.status === 'rascunho') && (
              <div className="sticky bottom-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur">
                {modalidade === 'ordem_prioridade' ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    {capacidadeKwpSelecionada > 0 ? (
                      <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                        {percentDaCapacidadeUg.toFixed(2)}% da capacidade da UG
                        <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">
                          {formatKwh(somaConsumoMedioUcs)} kWh de {formatKwh(capacidadeKwpSelecionada)} kWp
                        </span>
                      </span>
                    ) : (
                      <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{soma.toFixed(2)}% UCs</span>
                    )}
                    {ancoraKwh > 0.005 && (
                      <span className="flex items-center gap-1 text-sm text-primary">
                        <Landmark size={14} /> + {formatKwh(ancoraKwh)} kWh ({ancoraPercent.toFixed(2)}%) para a Unidade Âncora
                      </span>
                    )}
                    <ProjecaoCarteira {...projecaoCarteira} />
                  </div>
                ) : (
                  <div className="flex items-center gap-3 flex-wrap">
                    {habilitaSalvar && capacidadeKwpSelecionada > 0 ? (
                      <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                        {percentDaCapacidadeUg.toFixed(2)}% da capacidade da UG
                        <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">
                          {formatKwh(somaConsumoMedioUcs)} kWh de {formatKwh(capacidadeKwpSelecionada)} kWp
                        </span>
                      </span>
                    ) : (
                      <span className={`text-lg font-bold ${habilitaSalvar ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        {soma.toFixed(2)}%
                      </span>
                    )}
                    {!habilitaSalvar && (
                      <span className="flex items-center gap-1 text-sm text-amber-600 dark:text-amber-400">
                        <AlertTriangle size={14} />A soma é {soma.toFixed(2)}%. Ajuste os percentuais para fechar 100%.
                      </span>
                    )}
                    <ProjecaoCarteira {...projecaoCarteira} />
                  </div>
                )}
                <button
                  onClick={handleSalvar}
                  disabled={!habilitaSalvar || saving || excedenteAlocavelKwh <= 0}
                  className="px-5 py-2.5 rounded-lg bg-primary text-white font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && <Loader2 size={16} className="animate-spin" />}
                  Salvar Rateio
                </button>
              </div>
            )}

            {rateio && (
              <>
                <RateioStatusWorkflow rateio={rateio} ug={selectedUg} itens={itensSalvos} onChanged={handleWorkflowChanged} />
                <AuditoriaLogPanel rateioId={rateio.id} ugId={selectedUg.id} />
              </>
            )}
          </div>
        )}
      </div>

      {showUgForm && (
        <UGFormModal
          ug={editingUg}
          onClose={() => {
            setShowUgForm(false);
            setEditingUg(null);
          }}
          onSaved={handleUgSaved}
        />
      )}

      {showVincularModal && selectedUg && (
        <VincularUCsModal
          ug={selectedUg}
          ucsVinculadas={ucsVinculadas}
          onClose={() => setShowVincularModal(false)}
          onVinculosAlterados={handleVinculosAlterados}
        />
      )}
    </div>
  );
};

export default SimuladorRateioView;
